import {
    type FC,
    useState,
    useEffect,
    useCallback,
    type ReactNode,
    type MouseEventHandler,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { graphql, useFragment } from 'react-relay';
import { trackEvent } from 'dibs-tracking';
import { NavTab } from 'dibs-elements/exports/NavTab.tsx';
import { NavTabList } from 'dibs-elements/exports/NavTabList.tsx';
import { Badge } from 'dibs-elements/exports/Badge.tsx';

import { paths } from '../../../constants.ts';
import { type TabNames, type TabRoute } from '../useNftCollectorTabConfig.tsx';

import { type NftProfileCollectionNav_viewer$key } from './__generated__/NftProfileCollectionNav_viewer.graphql.ts';

function normalizePath(path: string): string {
    return path.replace(/\//g, '');
}

const getInitialTab = (tabs: TabRoute[], path: string): TabRoute => {
    const foundTab = tabs.find(tab => normalizePath(tab.path) === path);
    const defaultTab = tabs[0];
    return foundTab || defaultTab;
};

const NftProfileCollectionNavTab: FC<{
    name: string;
    href: string;
    onClick: MouseEventHandler;
    children: ReactNode;
}> = ({ name, href, children, onClick }) => {
    return (
        <NavTab name={name} href={href} onClick={onClick} tabSpacing="medium">
            {children}
        </NavTab>
    );
};

const viewerFragment = graphql`
    fragment NftProfileCollectionNav_viewer on Viewer {
        publicProfile: publicProfile(username: $username) {
            username
        }
    }
`;

export const NftProfileCollectionNav: FC<{
    tabConfig: TabRoute[];
    viewer: NftProfileCollectionNav_viewer$key;
}> = ({ tabConfig, viewer: viewerRef }) => {
    const history = useHistory();
    const location = useLocation();

    const viewer = useFragment(viewerFragment, viewerRef);

    const [tab, setTab] = useState(getInitialTab(tabConfig, normalizePath(location.pathname)).name);

    useEffect(() => {
        const initialPath = normalizePath(location.pathname);
        const tabByInitialPath = getInitialTab(tabConfig, initialPath);
        const firstTab = tabConfig[0];
        if (tabByInitialPath === firstTab) {
            history.replace(firstTab.path);
        }
        setTab(tabByInitialPath.name);
        // set initial tab by url on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = useCallback(
        (activeTabName: TabNames): MouseEventHandler =>
            e => {
                if (e.ctrlKey || e.shiftKey || e.metaKey || (e.button && e.button === 1)) {
                    return;
                }
                e.preventDefault();
                const tabPath = tabConfig.find(({ name }) => name === activeTabName)?.path;
                if (tabPath) {
                    setTab(activeTabName);
                    history.replace(tabPath);

                    const trackLabels: Record<TabNames, string> = {
                        CREATOR_ITEMS_TAB: 'creations',
                        COLLECTION_TAB: 'collection',
                        CREATOR_DRAFTS_TAB: 'drafts',
                        BIDS_TAB: 'bids',
                    };
                    const label = trackLabels[activeTabName as TabNames];

                    trackEvent({
                        category: 'promo interaction',
                        action: 'NFT profile page click',
                        label: `${label} module`,
                        noninteraction: 0,
                    });
                }
            },
        [setTab, history, tabConfig]
    );

    return (
        <NavTabList
            contrast={false}
            underline={false}
            indicatorHeight="2px"
            indicatorPadding="xSmall"
            inactiveFont="mediumBody"
            activeFont="mediumBodyHeavy"
            fullWidth
            activeTab={tab}
            setActiveTab={() => {}}
            align="left"
        >
            {tabConfig.map(({ path, name, title, totalResults }) => (
                <NftProfileCollectionNavTab
                    onClick={handleClick(name)}
                    name={name}
                    href={
                        viewer.publicProfile?.username
                            ? `${paths.profileUser}${viewer.publicProfile.username}${path}`
                            : '/'
                    }
                    key={path}
                >
                    {title} <Badge>{totalResults}</Badge>
                </NftProfileCollectionNavTab>
            ))}
        </NavTabList>
    );
};
